import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LoaderIcon } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { mcpApi } from '@/api/mcp.api';
import {
  emptyMCPInstanceForm,
  isMCPInstanceFormValid,
  toMCP,
  type MCPInstanceFormState,
} from '@/pages/mcp/mcp-instance-form.utils';
import { MCPInstanceForm } from '@/pages/mcp/MCPInstanceForm';

export default function CreateMCPInstancePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<MCPInstanceFormState>({ ...emptyMCPInstanceForm });

  const createMutation = useMutation({
    mutationFn: (f: MCPInstanceFormState) => mcpApi.createMCP(toMCP(f)),
    onSuccess: (mcp) => {
      queryClient.invalidateQueries({ queryKey: ['mcps'] });
      navigate(`/plugins/mcps/instances/${mcp.id}`);
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/plugins/mcps?tab=instance')}
            className="h-8 w-8 p-0"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">新建 MCP 实例</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              直接配置一个独立的 MCP 实例。如果想基于现有的 MCP 模版创建，请到 MCP
              模版详情页点击"创建实例"。
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/plugins/mcps?tab=instance')}>
            取消
          </Button>
          <Button
            disabled={!isMCPInstanceFormValid(form) || createMutation.isPending}
            onClick={() => createMutation.mutate(form)}
          >
            {createMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4" />}创建 MCP 实例
          </Button>
        </div>
      </div>

      <MCPInstanceForm form={form} onChange={setForm} />
    </div>
  );
}
